import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import useAssignmentChatThread from 'hooks/chat/useAssignmentChatThread';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import chatService from 'services/chat';
import {
    buildSubmissionCreatedSystemMessage,
    hasSubmissionCreatedSystemMessage,
    sortChatMessagesByCreatedOn,
    sortSubmissionsByCreatedOn,
} from 'utils/chat';

const buildSubmissionReference = (submission) => {
    return {
        id: submission?.id,
        submission_id: submission?.id,
    };
};

const buildExpectedSubmissionCreatedSystemMessages = (submissions = []) => {
    return sortSubmissionsByCreatedOn(submissions).map((submission, index) => {
        return buildSubmissionCreatedSystemMessage(submission, index);
    });
};

/**
 * Role-specific thread setup for students viewing their own submission.
 *
 * Use from student submission chat panels. It creates/resolves the student's assignment chat,
 * keeps submission-created system messages in sync with drafts, passes the current file key
 * to AI requests, and loads feedback-aware prompt suggestions.
 *
 * @param {Object} props
 * @param {Object|null} props.currentSubmission Submission currently shown to the student.
 * @param {boolean} props.isOpen Whether the chat panel is mounted/open.
 * @param {Function} [props.onChatUpdated] Notified whenever local chat state changes.
 * @param {Function} [props.resolveChat] Optional resolver used instead of creating a chat.
 * @param {Function} [props.shouldShowChatSuggestions] Predicate passed into the thread hook.
 * @returns {Object} `useAssignmentChatThread` result plus `fileKey` and student `viewModel`.
 */
const useStudentSubmissionChat = ({
    currentSubmission,
    isOpen,
    onChatUpdated,
    resolveChat,
    shouldShowChatSuggestions,
}) => {
    const { currentAssignment, studentSubmissions } = useApp();
    const { auth } = useAuth();
    const { t } = useTranslation('assignment');

    const currentSubmissionFileKey = currentSubmission?.files?.[0]?.key;
    const currentSubmissionReference = useMemo(() => buildSubmissionReference(currentSubmission), [currentSubmission]);
    const currentAssignmentRef = useRef(currentAssignment);
    const currentSubmissionReferenceRef = useRef(currentSubmissionReference);
    const expectedSubmissionCreatedSystemMessages = useMemo(() => {
        return buildExpectedSubmissionCreatedSystemMessages(studentSubmissions || []);
    }, [studentSubmissions]);

    const StudentChatViewModel = {
        header: {
            title: t('chat.student-title', 'Welcome'),
            infoTooltip: t(
                'chat.student-tooltip',
                'This feature is here to help you inquire further. Please note that this chat is visible to your instructor.'
            ),
        },
    };

    useEffect(() => {
        currentAssignmentRef.current = currentAssignment;
        currentSubmissionReferenceRef.current = currentSubmissionReference;
    }, [currentAssignment, currentSubmissionReference]);

    const reconcileSubmissionCreatedSystemMessages = useCallback(
        async (assignmentChat) => {
            if (!assignmentChat || expectedSubmissionCreatedSystemMessages.length === 0) return assignmentChat;

            let nextAssignmentChat = assignmentChat;

            for (const submissionCreatedMessage of expectedSubmissionCreatedSystemMessages) {
                const hasSystemMessage = hasSubmissionCreatedSystemMessage({
                    chat: nextAssignmentChat,
                    draftNumber: submissionCreatedMessage.system_data?.draftNumber,
                    messageId: submissionCreatedMessage.id,
                });

                if (hasSystemMessage) continue;

                const pushMessageResponse = await chatService.pushMessage({
                    auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
                    chat_id: nextAssignmentChat.id,
                    message: submissionCreatedMessage,
                });

                const reconciledMessage = pushMessageResponse?.message || submissionCreatedMessage;
                const isMessageAlreadyInLoadedPage = (nextAssignmentChat.messages || []).some(
                    ({ id }) => id === reconciledMessage.id
                );

                if (pushMessageResponse?.duplicated && !isMessageAlreadyInLoadedPage) {
                    continue;
                }

                nextAssignmentChat = {
                    ...nextAssignmentChat,
                    modified_on: reconciledMessage.modified_on || nextAssignmentChat.modified_on,
                    messages: sortChatMessagesByCreatedOn([...(nextAssignmentChat.messages || []), reconciledMessage]),
                };
            }

            return nextAssignmentChat;
        },
        [expectedSubmissionCreatedSystemMessages]
    );

    const requestChatSuggestions = useCallback(() => {
        return chatService.getSuggestions({
            auth_token: `Bearer ${localStorage.getItem('auth_token')}`,
            assignment: currentAssignmentRef.current,
            submission: currentSubmissionReferenceRef.current,
            count: 3,
        });
    }, []);

    const assignmentChatThread = useAssignmentChatThread({
        assignmentId: currentAssignment?.id,
        courseId: auth?.course_id,
        ownerAccountId: auth?.id,
        readerAccountId: auth?.owner_account_id || auth?.id,
        createSuggestionsRequest: requestChatSuggestions,
        initializeChat: reconcileSubmissionCreatedSystemMessages,
        isOpen,
        onChatUpdated,
        resolveChat,
        shouldShowSuggestions: shouldShowChatSuggestions,
    });

    return {
        ...assignmentChatThread,
        fileKey: currentSubmissionFileKey,
        viewModel: StudentChatViewModel,
    };
};

export default useStudentSubmissionChat;
